import { useNavigation } from '@react-navigation/native';
import { Headline3 } from 'aimo-ui';
import AccountContext from 'context/AccountContext';
import ParkingContext from 'context/ParkingContext';
import useAfterAuthenticationRedirect from 'hooks/useAfterAuthenticationRedirect';
import useIsLoggedIn from 'hooks/useIsLoggedIn';
import useParkingPricingInfo from 'hooks/useParkingPricingInfo';
import useReadPurchasedPrepaidTickets from 'hooks/useReadPurchasedPrepaidTickets';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import styled from 'styled-components/native';
import {
  ColumnWithMargins,
  ScreenContainer,
  StyledWrapper,
} from 'styles/ContainerStyles';
import { ResolvedPermit } from 'types/common';
import {
  ParkZoneBenefit,
  Pmc,
  PredefinedTicket,
  UnifiedPermit,
} from 'types/generatedSchemaTypes';
import { usePermitStore } from 'stores/permitStore';
import { ParkNavProps, RootNavProps } from 'types/navigation';
import {
  compilePmcDetails,
  handlePermitConditions,
  isPmcAvailable,
  resolvePermitDetails,
  resolvePmcMemberGroupLock,
} from 'utils/permitUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import PrepaidTicketOrPermitItem from './PrepaidTicketOrPermitItem';

const HeaderWrapper = styled.View`
  margin-bottom: 12px;
`;

const PrepaidTicketAndPermitList = () => {
  const { navigate } = useNavigation<RootNavProps>();
  const { navigate: navigatePark } = useNavigation<ParkNavProps>();
  const {
    parkingZoneResult: parkingZone,
    mySalesOrdersForZone = [],
    setSelectedParkzonePmcUnion,
    availablePermits,
  } = useContext(ParkingContext);
  const {
    customer: { householdBenefits, permits },
    benefitsLoading: benefitsQueryLoading,
  } = useContext(AccountContext);
  const pmcStatusDetail = compilePmcDetails(permits ?? []);
  const { setReturnRedirectToBuyShortTermParking, navigateToAccountLogin } =
    useAfterAuthenticationRedirect();
  const { isLoggedIn } = useIsLoggedIn();

  const { uid = '' } = parkingZone?.data?.parkZone ?? {};
  const countryCode = parkingZone?.data?.parkZone?.countryCode ?? 'FI';
  const { predefinedTickets } = useParkingPricingInfo(uid, true);
  const { purchasedPrepaidTickets } = useReadPurchasedPrepaidTickets({});
  const prepaidTickets = predefinedTickets.filter(
    (predefinedTicket) => predefinedTicket?.sessionType === 'PREPAID'
  );
  const parkingZoneBenefits = (
    parkingZone?.data?.parkZone?.benefits ?? []
  ).filter((b: any): b is ParkZoneBenefit => !!b);
  const benefitsLoading = parkingZone?.loading || benefitsQueryLoading;

  const parkzonesPermits = availablePermits || [];
  const { t } = useTranslation();

  const onPrepaidItemSelect = (prepaidTicket: PredefinedTicket) => {
    if (!isLoggedIn) {
      setReturnRedirectToBuyShortTermParking(
        parkingZone?.data?.parkZone?.uid as string
      );
      navigateToAccountLogin();
      return;
    }
    if (prepaidTicket.endTime) {
      setSelectedParkzonePmcUnion(prepaidTicket);
      navigatePark('parkPrepaidTicketsAndPermitsEdit', {
        parkingZoneUid: uid,
        id: prepaidTicket.uid || '',
      });
      return;
    }

    setSelectedParkzonePmcUnion(prepaidTicket);
    setSelectedPrepaidStartDate(undefined);

    navigatePark('parkPrepaidTicketsAndPermitsBuy', {
      parkingZoneUid: uid,
      id: prepaidTicket.uid || '',
    });
  };

  const onPurchasedPermitSelect = (permit: UnifiedPermit) => {
    setSelectedParkzonePmcUnion(permit);
    setSelectedPermit(permit.id);

    if (permit.permitType === 'WAITING_LIST') {
      return navigate('longTermParking', {
        screen: 'waitingListPermitDetails',
        params: { id: permit.id },
      });
    }
    navigate('salesOrderPermitEdit', {
      orderLineIdWithVersion: permit.orderLineIdWithVersion || permit.id,
    });
  };

  const {
    setPmcIsLockedByMemberGroup,
    resetPmcIsLockedByMemberGroup,
    setSelectedPermit,
    setSelectedPmc,
    setSelectedPrepaidStartDate,
    setSelectedParkZoneBenefit,
  } = usePermitStore();

  const onPmcSelect = (
    permit: ResolvedPermit,
    pmcIsLockedForTheCustomer?: boolean,
    memberGroupBenefit?: ParkZoneBenefit | undefined,
    pmcIsInsideALockedMemberGroup?: boolean
  ) => {
    if (!isLoggedIn) {
      setReturnRedirectToBuyShortTermParking(
        parkingZone?.data?.parkZone?.uid as string
      );
      navigateToAccountLogin();
      return;
    }

    const {
      isWaitingActivation,
      exceedMaximumBenefit,
      isDuplicatePermit,
      orderLineIdWithVersion,
      ...pmcProperties
    } = permit;

    const pmc: Pmc = {
      ...pmcProperties,
    };

    setSelectedPermit(orderLineIdWithVersion);
    setSelectedParkzonePmcUnion(permit);
    setSelectedPmc(pmc);
    setSelectedParkZoneBenefit(memberGroupBenefit);
    resetPmcIsLockedByMemberGroup();
    if (pmc.pmcId) {
      setPmcIsLockedByMemberGroup(
        pmc.pmcId,
        !!pmcIsLockedForTheCustomer,
        !!pmcIsInsideALockedMemberGroup
      );
    }

    if (
      handlePermitConditions(
        permit,
        pmc,
        'PredefinedTicket',
        !!pmcIsLockedForTheCustomer,
        !!pmcIsInsideALockedMemberGroup
      )
    ) {
      return;
    }

    if (isPmcAvailable(pmc).showWaitingList) {
      return navigate('longTermParking', {
        screen: 'waitingListInfo',
        params: {
          parkingZoneUid: uid,
          pmcId: pmc.pmcId ?? '',
        },
      });
    }

    navigate('longTermParking', {
      screen: 'buyScreen',
      params: {
        parkingZoneUid: uid,
        pmcId: pmc.pmcId ?? '',
      },
    });
  };

  const permitOccurrenceMap = parkzonesPermits.map((permit) => ({
    ...permit,
    ...resolvePermitDetails(
      permit,
      householdBenefits ?? undefined,
      pmcStatusDetail
    ),
  }));

  const hasPurchasedPermits = mySalesOrdersForZone.length > 0;
  return (
    <ScreenContainer>
      {(purchasedPrepaidTickets.length > 0 || hasPurchasedPermits) && (
        <StyledWrapper mb={25} testID="prepaid-purchased-tickets-container">
          <HeaderWrapper>
            <Headline3>
              {t('parking.prepaidTickets.prepaidOwnedHeader')}
            </Headline3>
          </HeaderWrapper>
          {purchasedPrepaidTickets.map((item) => (
            <ColumnWithMargins key={item?.uid} mt={8} mb={8}>
              <PrepaidTicketOrPermitItem
                testID={`prepaid-owned-ticket-${item?.uid}`}
                item={item}
                countryCode={countryCode}
                onPress={onPrepaidItemSelect}
              />
            </ColumnWithMargins>
          ))}
          {mySalesOrdersForZone?.map((item) => (
            <ColumnWithMargins key={item?.id} mt={8} mb={8}>
              <PrepaidTicketOrPermitItem
                testID={`prepaid-owned-ticket-${item?.id}`}
                item={item as UnifiedPermit}
                countryCode={countryCode}
                onPress={onPurchasedPermitSelect}
              />
            </ColumnWithMargins>
          ))}
        </StyledWrapper>
      )}

      {prepaidTickets.length > 0 && (
        <StyledWrapper
          mb={25}
          {...testIdentifiers('prepaid-tickets-container')}>
          <HeaderWrapper>
            <Headline3>
              {t('parking.prepaidTickets.prepaidListHeader')}
            </Headline3>
          </HeaderWrapper>
          <View {...testIdentifiers('prepaid-tickets-container-items')}>
            {prepaidTickets.map((item) => (
              <ColumnWithMargins
                key={'prepaid-' + item?.uid}
                mt={8}
                mb={8}
                {...testIdentifiers('prepaid-ticket-item')}>
                <PrepaidTicketOrPermitItem
                  testID={`prepaid-ticket-${item?.uid}`}
                  item={item as PredefinedTicket}
                  countryCode={countryCode}
                  onPress={onPrepaidItemSelect}
                />
              </ColumnWithMargins>
            ))}
          </View>
        </StyledWrapper>
      )}

      {parkzonesPermits.length > 0 && (
        <>
          <HeaderWrapper>
            <Headline3>{t('services.permits')}</Headline3>
          </HeaderWrapper>
          {permitOccurrenceMap.map((permit) => {
            const {
              memberGroupBenefit,
              pmcIsInsideALockedMemberGroup,
              pmcIsLockedForTheCustomer,
            } = resolvePmcMemberGroupLock(
              permit.pmcId,
              parkingZoneBenefits,
              householdBenefits,
              permit.visibility
            );

            return (
              <ColumnWithMargins
                key={permit.id}
                mt={8}
                mb={8}
                {...testIdentifiers('permit-item')}>
                <PrepaidTicketOrPermitItem
                  testID={`prepaid-ticket-${permit.id}`}
                  item={permit}
                  countryCode={countryCode}
                  onPress={() =>
                    onPmcSelect(
                      permit as ResolvedPermit,
                      pmcIsLockedForTheCustomer,
                      memberGroupBenefit,
                      pmcIsInsideALockedMemberGroup
                    )
                  }
                  pmcIsLockedForTheCustomer={pmcIsLockedForTheCustomer}
                  pmcIsInsideALockedMemberGroup={pmcIsInsideALockedMemberGroup}
                  disabled={benefitsLoading}
                />
              </ColumnWithMargins>
            );
          })}
        </>
      )}
    </ScreenContainer>
  );
};

export default PrepaidTicketAndPermitList;
